import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Phone } from 'lucide-react';

const QUICK_COUNTRY_CODES = [
  { code: '+673', country: 'Brunei', flag: '🇧🇳' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
];

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { googleExchange, googleComplete, setAuthFromGoogle } = useAuth();

  // useRef (not useState) for processed flag — safe under React StrictMode.
  // Set synchronously at the start so the double-invoke in dev doesn't re-run.
  const hasProcessed = useRef(false);

  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [countryCode, setCountryCode] = useState('+673');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/auth', { replace: true });
      return;
    }

    (async () => {
      try {
        const data = await googleExchange(sessionId);
        if (data.requires_phone) {
          setPendingData(data);
          setShowPhonePrompt(true);
        } else {
          setAuthFromGoogle(data);
          const restoreUrl = localStorage.getItem('sessionRestoreUrl');
          if (restoreUrl) {
            localStorage.removeItem('sessionRestoreUrl');
            navigate(restoreUrl, { replace: true });
          } else {
            const bookingIntent = localStorage.getItem('bookingIntent');
            navigate(bookingIntent ? '/book' : '/home', { replace: true });
          }
        }
      } catch (err) {
        const msg =
          err?.response?.data?.detail || 'Google sign-in failed. Please try again.';
        toast.error(msg);
        navigate('/auth', { replace: true });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompleteSignup = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      setError('Phone number is required');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const fullPhone = countryCode + phone.trim();
      const data = await googleComplete(pendingData.session_id, fullPhone);
      setAuthFromGoogle(data);
      toast.success('Welcome to SmashZone!');
      const bookingIntent = localStorage.getItem('bookingIntent');
      navigate(bookingIntent ? '/book' : '/home', { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || 'Failed to complete sign-up. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading screen ──────────────────────────────────────────────────────────
  if (!showPhonePrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">Signing you in with Google...</p>
        </div>
      </div>
    );
  }

  // ── Phone number prompt (new Google user) ───────────────────────────────────
  return (
    <div className="mobile-container min-h-screen flex flex-col">
      {/* Hero strip matching AuthPage */}
      <div
        className="relative h-40 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1761286753703-570ed91dd90e?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/70 to-emerald-600/50" />
        <div className="relative h-full flex flex-col justify-end p-6 text-white">
          <h1 className="text-4xl font-black font-heading uppercase italic tracking-tight">
            SmashZone
          </h1>
        </div>
      </div>

      <div className="flex-1 p-6 bg-background">
        {/* Profile picture + greeting */}
        <div className="flex items-center gap-4 mb-8">
          {pendingData?.picture ? (
            <img
              src={pendingData.picture}
              alt="Google profile"
              className="w-14 h-14 rounded-full border-2 border-emerald-200 object-cover"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <span className="text-emerald-700 font-bold text-xl">
                {(pendingData?.name || 'G')[0].toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <h2 className="text-xl font-black uppercase font-heading">One last step</h2>
            <p className="text-sm text-muted-foreground">
              Hi <span className="font-semibold text-foreground">{pendingData?.name}</span>! We need your phone number.
            </p>
          </div>
        </div>

        <form onSubmit={handleCompleteSignup} className="space-y-5" data-testid="google-phone-form">
          <div>
            <Label className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
              Phone Number
            </Label>
            <div className="flex gap-2">
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger
                  data-testid="google-country-code-select"
                  className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary w-[140px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {QUICK_COUNTRY_CODES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="flex items-center gap-2">
                        <span>{c.flag}</span>
                        <span>{c.code}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative flex-1">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  data-testid="google-phone-input"
                  type="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                  required
                  autoFocus
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Enter phone number without country code
            </p>
            {error && (
              <p className="text-xs text-red-500 mt-1.5" data-testid="google-phone-error">
                {error}
              </p>
            )}
          </div>

          <Button
            type="submit"
            data-testid="google-complete-button"
            className="w-full btn-volt h-14 mt-6"
            disabled={submitting}
          >
            {submitting ? 'Creating Account...' : 'Complete Sign-Up'}
          </Button>

          <button
            type="button"
            onClick={() => navigate('/auth', { replace: true })}
            className="w-full text-sm text-muted-foreground hover:text-foreground text-center py-2"
          >
            Use email instead
          </button>
        </form>
      </div>
    </div>
  );
}
